import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CalculatorOutlined, DownOutlined, ExportOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Dropdown,
    Empty,
    Grid,
    Menu,
    Progress,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import moment from 'moment';
import qs from 'qs';

import PageTitle from '@/components/PageTitle';
import StatisticNumber from '@/components/StatisticNumber';
import TableActionBar from '@/components/TableActionBar';
import useExcelExportLite from '@/hooks/useExcelExportLite';
import { cleanObject } from '@/utils/helpers';
import { renderServiceGroup } from '@/utils/serviceGroups';
import { mapUserOptionLabels } from '@/utils/userOptionHelpers';
import {
    fetchCommissionDashboard,
    fetchCommissionDashboardAdjustments,
    fetchCommissionDashboardHolds,
    fetchCommissionDashboardLedger,
    fetchCommissionDashboardPayments,
    fetchCommissionDashboardUpcoming,
    exportCommissionDashboardExcel,
    recalculateCommission,
} from '@/redux/actions/commissionActions';
import { openCommissionActionModal, openCommissionRecalculationResult } from '@/utils/commission/actionModal';
import { hasPermission } from '@/utils/permission';

import CommissionDashboardDetailDrawer from './CommissionDashboardDetailDrawer';
import {
    CommissionCustomerColumn,
    CommissionCustomerServiceDetailProvider,
    CommissionServiceColumn,
} from './CommissionCustomerServiceColumns';
import {
    commissionStatus,
    formatMissingConditionLabels,
    formatCommissionMoney,
    formatCommissionRate,
    formatNextTriggerLabel,
} from './commissionDashboardHelpers';
import './CommissionDashboard.css';

const { Text } = Typography;

const projectionStatusLabels = {
    WAITING_EVENT: 'Chờ sự kiện',
    WAITING_RELATIVE_DATE: 'Chờ đến ngày',
    WAITING_PAYMENT: 'Chờ thanh toán',
    WAITING_SERVICE_CONDITION: 'Chờ điều kiện dịch vụ',
    REVENUE_HELD: 'Doanh thu đang tạm giữ',
    ROUTING_UNRESOLVED: 'Chưa phân bổ được người hưởng',
    ALLOCATION_HELD: 'Doanh thu nhân viên đang tạm giữ',
    READY_TO_RECOGNIZE: 'Chờ hệ thống ghi nhận',
};

const responsiveColumns = columns => columns.map(column => ({
    ...column,
    fixed: undefined,
    onCell: record => ({
        ...(column.onCell ? column.onCell(record) : {}),
        'data-label': typeof column.title === 'string' ? column.title : '',
    }),
}));

const emptyDashboard = {
    filters: {
        employees: [],
    },
    summary: {},
    workflow: {},
};

export default function CommissionDashboard() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const dashboard = useSelector(
        state => state.commissionReducer.dashboard || emptyDashboard
    );
    const ledger = useSelector(state => state.commissionReducer.dashboardLedger || { data: [] });
    const adjustments = useSelector(state => state.commissionReducer.dashboardAdjustments || { data: [] });
    const payments = useSelector(state => state.commissionReducer.dashboardPayments || { data: [] });
    const upcoming = useSelector(state => state.commissionReducer.dashboardUpcoming || { data: [] });
    const holds = useSelector(state => state.commissionReducer.dashboardHolds || { data: [] });
    const users = useSelector(state => state.config.users || []);
    const authUser = useSelector(state => state.auth.authUser);

    const query = useMemo(
        () => qs.parse(location.search.slice(1)),
        [location.search]
    );

    const [loading, setLoading] = useState(false);
    const [detailItemId, setDetailItemId] = useState(null);
    const [recalculatingId, setRecalculatingId] = useState(null);
    const [isCalculationGuideExpanded, setIsCalculationGuideExpanded] = useState(false);
    const [sectionLoading, setSectionLoading] = useState({});
    const employeeOptions = useMemo(
        () => mapUserOptionLabels(dashboard.filters?.employees || [], users, 'id'),
        [dashboard.filters?.employees, users]
    );

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const sectionHeadStyle = {
        background: '#fafafa',
        minHeight: 42,
        padding: isMobile ? '0 12px' : '0 16px',
    };

    const sectionBodyStyle = {
        padding: isMobile ? 8 : 16,
        minWidth: 0,
    };

    const sectionFilters = useCallback((name, filters, pagination = {}) => {
        const scope = {
            employee_id: filters.employee_id || undefined,
            period_key: filters.period_key || undefined,
            branch_id: filters.branch_id || undefined,
        };

        // Backend quyết định toàn bộ candidate, thứ tự và giới hạn top 10 của box thanh toán.
        if (name === 'payments') return {
            employee_id: scope.employee_id,
            branch_id: scope.branch_id,
        };

        return {
            ...scope,
            page: pagination.current || 1,
            per_page: pagination.pageSize || 10,
        };
    }, []);

    const loadSection = useCallback(async (name, action, filters, pagination = {}) => {
        setSectionLoading(value => ({ ...value, [name]: true }));
        try {
            await dispatch(action(sectionFilters(name, filters, pagination)));
        } finally {
            setSectionLoading(value => ({ ...value, [name]: false }));
        }
    }, [dispatch, sectionFilters]);

    const loadDashboard = useCallback(
        async filters => {
            setLoading(true);

            try {
                await Promise.all([
                    dispatch(
                        fetchCommissionDashboard({
                            employee_id: filters.employee_id || undefined,
                            period_key: filters.period_key || undefined,
                            branch_id: filters.branch_id || undefined,
                        })),
                    loadSection('ledger', fetchCommissionDashboardLedger, filters),
                    loadSection('adjustments', fetchCommissionDashboardAdjustments, filters),
                    loadSection('payments', fetchCommissionDashboardPayments, filters),
                    loadSection('upcoming', fetchCommissionDashboardUpcoming, filters),
                    loadSection('holds', fetchCommissionDashboardHolds, filters),
                ]);
            } catch (error) {
                // Action đã xử lý thông báo lỗi.
            } finally {
                setLoading(false);
            }
        },
        [dispatch, loadSection]
    );

    useEffect(() => {
        loadDashboard(query);
    }, [loadDashboard, query]);

    const changeFilter = (name, value) => {
        const nextQuery = cleanObject({
            ...query,
            [name]: value,
            page: 1,
        });

        history.push({
            pathname: location.pathname,
            search: qs.stringify(nextQuery),
        });
    };

    const changeBranchFilter = value => {
        const nextQuery = cleanObject({
            ...query,
            branch_id: value || undefined,
            employee_id: undefined,
            page: 1,
        });

        history.push({
            pathname: location.pathname,
            search: qs.stringify(nextQuery),
        });
    };

    const paginationProps = (source, name, action) => ({
        current: source.current_page || 1,
        pageSize: source.per_page || 10,
        total: source.total || 0,
        showSizeChanger: !isMobile,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: (current, pageSize) => loadSection(name, action, query, { current, pageSize }),
    });

    const canRecalculate = hasPermission(authUser, 'commission_result', 'calculate');
    const confirmRecalculate = item => {
        openCommissionActionModal({
            action: 'rebuild',
            onOk: async () => {
                setRecalculatingId(item.customer_service_id);
                try {
                    const result = await dispatch(recalculateCommission(item.customer_service_id));
                    openCommissionRecalculationResult(result);
                    await loadDashboard(query);
                } finally {
                    setRecalculatingId(null);
                }
            },
        });
    };

    const summary = dashboard.summary || {};

    const employeeId =
        query.employee_id ||
        dashboard.filters?.selected_employee_id ||
        null;

    const periodKey =
        query.period_key ||
        dashboard.filters?.selected_period_key ||
        null;

    const branchId =
        query.branch_id ||
        dashboard.filters?.selected_branch_id ||
        null;

    const buildCurrentEmployeeExportQuery = useCallback(() => cleanObject({
        employee_id: employeeId || undefined,
        period_key: periodKey || undefined,
        branch_id: branchId || undefined,
    }), [branchId, employeeId, periodKey]);

    const buildAllEmployeesExportQuery = useCallback(() => cleanObject({
        period_key: periodKey || undefined,
        branch_id: branchId || undefined,
    }), [branchId, periodKey]);

    const [exportingCurrentEmployee, queueCurrentEmployeeExport] = useExcelExportLite(
        exportCommissionDashboardExcel,
        { buildQuery: buildCurrentEmployeeExportQuery }
    );

    const [exportingAllEmployees, queueAllEmployeesExport] = useExcelExportLite(
        exportCommissionDashboardExcel,
        { buildQuery: buildAllEmployeesExportQuery }
    );

    const periodLabel = periodKey
        ? moment(periodKey, 'YYYY-MM').format('MM/YYYY')
        : '';

    const selectedEmployee = (
        dashboard.filters?.employees || []
    ).find(
        item => Number(item.id) === Number(employeeId)
    );

    /**
     * SỔ HOA HỒNG
     */
    const ledgerColumns = [
        {
            title: 'Ngày',
            dataIndex: 'effective_at',
            width: '8%',
            render: value =>
                value
                    ? moment(value).format('DD/MM/YYYY')
                    : '—',
        },
        {
            title: 'Khách hàng / dịch vụ',
            key: 'customer_service',
            width: '24%',
            render: (_, item) => {
                return (
                    <Space
                        direction="vertical"
                        size={2}
                        style={{ width: '100%' }}
                    >
                        <CommissionCustomerColumn customer={item.customer} />

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 4,
                            }}
                        >
                            <Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                            >
                                Nhóm dịch vụ:
                            </Text>
                            {renderServiceGroup(
                                item.service?.group
                            ) || (
                                <Text style={{ fontSize: 12 }}>
                                    Chưa xác định
                                </Text>
                            )}
                        </div>

                        <CommissionServiceColumn customerServiceId={item.customer_service_id} service={item.service} />

                        {item.role_label && (
                            <Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                            >
                                Vai trò: {item.role_label}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Nguồn ghi nhận',
            dataIndex: 'event_label',
            width: '15%',
            render: value => value || '—',
        },
        {
            title: 'Hoa hồng',
            key: 'commission_amounts',
            width: '19%',
            render: (_, item) => {
                const rateDetails = Array.isArray(
                    item.commission_rate_details
                )
                    ? item.commission_rate_details
                    : [];
                const showRateBreakdown =
                    rateDetails.length > 1 ||
                    (rateDetails.length === 1 &&
                        Number(rateDetails[0].base_revenue) !==
                            Number(item.base_revenue));

                return (
                    <Space
                        direction="vertical"
                        size={0}
                        style={{ width: '100%' }}
                    >
                        <Text type="secondary">
                            Doanh thu tính:{' '}
                            <Text strong>
                                {formatCommissionMoney(
                                    item.base_revenue
                                )}
                            </Text>
                        </Text>

                        <Text type="secondary">
                            Tỷ lệ áp dụng:{' '}
                            {!showRateBreakdown && (
                                <Text strong>
                                    {rateDetails.length
                                        ? formatCommissionRate(
                                            rateDetails[0].rate_percent
                                        )
                                        : 'Không áp dụng tỷ lệ %'}
                                </Text>
                            )}
                        </Text>

                        {showRateBreakdown &&
                            rateDetails.map((detail, index) => (
                                <Text
                                    key={`${detail.rate_percent}-${index}`}
                                    type="secondary"
                                    style={{ paddingLeft: 12 }}
                                >
                                    {formatCommissionMoney(
                                        detail.base_revenue
                                    )}{' '}
                                    ×{' '}
                                    <Text strong>
                                        {formatCommissionRate(
                                            detail.rate_percent
                                        )}
                                    </Text>
                                </Text>
                            ))}

                        <Text>
                            Số tiền hoa hồng:{' '}
                            <Text strong>
                                {formatCommissionMoney(
                                    item.gross_amount
                                )}
                            </Text>
                        </Text>

                        {Number(item.hold_amount) > 0 && (
                            <Text type="secondary">
                                Tạm giữ:{' '}
                                {formatCommissionMoney(
                                    item.hold_amount
                                )}
                            </Text>
                        )}

                        {Number(item.adjustment_amount) !==
                            0 && (
                                <Text type="secondary">
                                    Điều chỉnh:{' '}
                                    {formatCommissionMoney(
                                        item.adjustment_amount
                                    )}
                                </Text>
                            )}

                        <Text>
                            Thực nhận:{' '}
                            <Text strong>
                                {formatCommissionMoney(
                                    item.net_amount
                                )}
                            </Text>
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Thanh toán',
            key: 'payment',
            width: '17%',
            render: (_, item) => (
                <Space
                    direction="vertical"
                    size={0}
                >
                    <Text type="secondary">
                        Đã thanh toán:{' '}
                        {formatCommissionMoney(
                            item.paid_amount
                        )}
                    </Text>

                    <Text strong>
                        Còn được nhận:{' '}
                        {formatCommissionMoney(
                            item.remaining_payable
                        )}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: '11%',
            render: value => {
                const view = commissionStatus(value);

                return (
                    <Tag color={view.color}>
                        {view.label}
                    </Tag>
                );
            },
        },
        {
            title: '',
            key: 'action',
            width: '6%',
            render: (_, item) => (
                <Tooltip title="Xem chi tiết">
                    <Button
                        aria-label="Xem chi tiết khoản hoa hồng"
                        size="small"
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() =>
                            setDetailItemId(item.id)
                        }
                    />
                </Tooltip>
            ),
        },
    ];

    /**
     * TIẾN ĐỘ THANH TOÁN DỊCH VỤ
     */
    const paymentColumns = [
        {
            title: 'Khách hàng / dịch vụ',
            key: 'customer_service',
            width: '28%',
            render: (_, item) => <Space direction="vertical" size={2}>
                <CommissionCustomerColumn customer={item.customer} />
                <CommissionServiceColumn customerServiceId={item.customer_service_id} service={item.service} />
            </Space>,
        },
        {
            title: 'Giá trị dịch vụ',
            dataIndex: 'service_amount',
            align: 'right',
            width: '13%',
            render: formatCommissionMoney,
        },
        {
            title: 'Đã phân bổ',
            dataIndex: 'paid_amount',
            align: 'right',
            width: '12%',
            render: formatCommissionMoney,
        },
        {
            title: 'Còn phải thu',
            dataIndex: 'debt_amount',
            align: 'right',
            width: '13%',
            render: formatCommissionMoney,
        },
        {
            title: 'Tiến độ',
            dataIndex: 'payment_progress',
            width: '16%',
            render: value => (
                <div style={{ paddingRight: '10px' }}><Progress
                    percent={Number(value) || 0}
                    size="small"
                /></div>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'service_status_label',
            width: '11%',
            render: value => <Tag>{value || 'Chưa xác định'}</Tag>,
        },
        {
            title: 'Biến động',
            dataIndex: 'last_financial_activity_at',
            width: '12%',
            render: value =>
                value
                    ? moment(value).format('DD/MM/YYYY')
                    : '—',
        },
        ...(canRecalculate ? [{
            title: '#',
            key: 'recalculate',
            width: '6%',
            render: (_, item) => (
                <Tooltip title="Tính lại hoa hồng">
                    <Button
                        aria-label="Tính lại hoa hồng của dịch vụ"
                        size="small"
                        type="text"
                        icon={<CalculatorOutlined />}
                        loading={Number(recalculatingId) === Number(item.customer_service_id)}
                        disabled={recalculatingId !== null && Number(recalculatingId) !== Number(item.customer_service_id)}
                        onClick={() => confirmRecalculate(item)}
                    />
                </Tooltip>
            ),
        }] : []),
    ];

    /**
     * HOA HỒNG DỰ KIẾN
     */
    const upcomingColumns = [
        {
            title: 'Khách hàng / dịch vụ',
            key: 'customer_service',
            width: '18%',
            render: (_, item) => <Space direction="vertical" size={2}>
                <CommissionCustomerColumn record={item} />
                <CommissionServiceColumn customerServiceId={item.customer_service_id} serviceName={item.service_name} />
            </Space>,
        },
        {
            title: 'Vai trò hưởng',
            dataIndex: 'role_label',
            key: 'role_label',
            width: '8%',
        },
        {
            title: 'Mốc / trạng thái hiện tại',
            key: 'milestone_status',
            width: '15%',
            render: (_, item) => (
                <Space direction="vertical" size={0}>
                    <Text>{item.condition_label || '—'}</Text>
                    <Text type="secondary">
                        {projectionStatusLabels[item.status] || item.status || 'Đang chờ'}
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Lý do chưa tính / bước tiếp theo',
            key: 'reason',
            width: '23%',
            render: (_, item) => (
                <Space direction="vertical" size={0}>
                    <Text>{item.reason || 'Chưa đủ điều kiện ghi nhận.'}</Text>
                    <Text type="secondary">
                        Bước tiếp theo: {formatNextTriggerLabel(item.next_trigger_label)}
                    </Text>
                    {item.missing_condition_labels?.length > 0 && (
                        <Text type="secondary">
                            {formatMissingConditionLabels(item.missing_condition_labels)}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Kỳ / ngày dự kiến',
            dataIndex: 'due_date',
            width: '12%',
            render: (value, item) => value
                ? moment(value).format('DD/MM/YYYY')
                : `${formatNextTriggerLabel(item.next_trigger_label || item.condition_label)} / chưa xác định ngày`,
        },
        {
            title: 'Thanh toán / phân bổ',
            key: 'payment_allocation',
            width: '14%',
            render: (_, item) => (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Progress percent={Number(item.payment_progress) || 0} size="small" />
                    <Text type="secondary">
                        Đã thu {formatCommissionMoney(item.paid_amount)} / {formatCommissionMoney(item.service_amount)}
                    </Text>
                    <Text type="secondary">
                        Phân bổ: {Number(item.allocation_percent) || 0}%
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Hoa hồng dự kiến',
            key: 'expected_amount',
            align: 'right',
            width: '10%',
            render: (_, item) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{formatCommissionMoney(item.expected_amount)}</Text>
                    {Number(item.expected_hold_amount) > 0 && (
                        <Text type="secondary">
                            Dự kiến giữ: {formatCommissionMoney(item.expected_hold_amount)}
                        </Text>
                    )}
                </Space>
            ),
        },
    ];

    const filters = [
        <Select
            key="branch_id"
            value={branchId ? Number(branchId) : undefined}
            style={{ width: '260px' }}
            className="table-button"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Tất cả cơ sở"
            options={(dashboard.filters?.branches || []).map(item => ({
                value: item.id,
                label: item.name,
            }))}
            onChange={changeBranchFilter}
        />,
        <Select
            key="employee_id"
            value={
                employeeId
                    ? Number(employeeId)
                    : undefined
            }
            style={{ width: '300px' }}
            className="table-button"
            showSearch
            optionFilterProp="label"
            disabled={
                !dashboard.filters
                    ?.can_view_all_employees
            }
            placeholder="Chọn nhân sự"
            options={employeeOptions.map(item => ({
                value: item.id,
                label: item.label,
            }))}
            onChange={value =>
                changeFilter('employee_id', value)
            }
        />,

        <DatePicker
            key="period_key"
            picker="month"
            allowClear={false}
            value={
                periodKey
                    ? moment(periodKey, 'YYYY-MM')
                    : null
            }
            format="MM/YYYY"
            placeholder="Chọn tháng"
            style={{ width: '300px' }}
            className="table-button"
            onChange={value =>
                value &&
                changeFilter(
                    'period_key',
                    value.format('YYYY-MM')
                )
            }
        />,
    ];

    return (
        <CommissionCustomerServiceDetailProvider>
        <div
            style={{
                maxWidth: '100%',
                minWidth: 0,
            }}
        >
            <PageTitle
                routes={[
                    {
                        path: '/',
                        breadcrumbName: 'Trang chủ',
                    },
                    {
                        path: '/commissions',
                        breadcrumbName:
                            'Hoa hồng của tôi',
                    },
                ]}
                title="Hoa hồng của tôi"
            />

            <TableActionBar
                isShowAddButton={false}
                isShowDeleteButton={false}
                showSearch={false}
                showFilter={false}
            >
                {filters}
                <Button
                    block={isMobile}
                    className="table-button"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() =>
                        loadDashboard(query)
                    }
                >
                    Làm mới
                </Button>
                {dashboard.filters?.can_view_all_employees ? (
                    <Dropdown
                        trigger={['click']}
                        overlay={(
                            <Menu>
                                <Menu.Item key="current" onClick={queueCurrentEmployeeExport}>
                                    Xuất nhân viên đang xem
                                </Menu.Item>
                                <Menu.Item key="all" onClick={queueAllEmployeesExport}>
                                    Xuất tất cả nhân viên theo cơ sở
                                </Menu.Item>
                            </Menu>
                        )}
                    >
                        <Button
                            block={isMobile}
                            className="table-button"
                            icon={<ExportOutlined />}
                            loading={exportingCurrentEmployee || exportingAllEmployees}
                        >
                            Xuất Excel <DownOutlined />
                        </Button>
                    </Dropdown>
                ) : (
                    <Button
                        block={isMobile}
                        className="table-button"
                        icon={<ExportOutlined />}
                        loading={exportingCurrentEmployee}
                        onClick={queueCurrentEmployeeExport}
                    >
                        Xuất Excel
                    </Button>
                )}
            </TableActionBar>
            <Alert
                type="info"
                style={{ marginBottom: 16 }}
                message="Cách tính hoa hồng"
                description={
                    <Space direction="vertical" size={6}>
                        <Text>
                            Hoa hồng được tính từ phần doanh thu đáp ứng chính sách, phân bổ cho đúng
                            người hưởng và áp dụng cách tính theo từng chính sách.
                        </Text>

                        {isCalculationGuideExpanded && (
                            <>
                                <Text>
                                    <Text strong>1. Xét điều kiện:</Text> Chỉ phần doanh thu đáp ứng chính sách
                                    và đã trừ các khoản giảm trừ hợp lệ mới được đưa vào tính.
                                </Text>

                                <Text>
                                    <Text strong>2. Phân bổ:</Text> Doanh thu đủ điều kiện được phân bổ cho
                                    người hưởng theo phân công và thỏa thuận áp dụng. Phần chưa xác định được
                                    người hưởng hoặc chưa đủ điều kiện phân bổ sẽ được tạm giữ.
                                </Text>

                                <Text>
                                    <Text strong>3. Tính hoa hồng:</Text> Chính sách xác định cơ sở tính phù hợp
                                    (giá trị dịch vụ, khoản thu hợp lệ hoặc phần doanh thu đã phân bổ), rồi áp
                                    dụng tỷ lệ, số lượng × đơn giá, khoản cố định hoặc mức lũy tiến.
                                </Text>

                                <Text>
                                    <Text strong>4. Giữ và mở giữ:</Text> Một phần doanh thu hoặc hoa hồng có thể
                                    được tạm giữ; khi điều kiện mở giữ của chính sách được đáp ứng, phần này được
                                    ghi nhận tiếp theo đúng điều kiện áp dụng.
                                </Text>

                                <Text>
                                    <Text strong>5. Kết quả cuối:</Text> Cộng các khoản thưởng hoặc điều chỉnh tăng
                                    đã được duyệt; trừ điều chỉnh giảm và các khoản truy thu phát sinh do hoàn,
                                    hủy hoặc điều chỉnh nguồn thu.
                                </Text>
                            </>
                        )}

                        <Button
                            type="link"
                            size="small"
                            block={isMobile}
                            style={{ padding: 0, textAlign: 'left' }}
                            onClick={() =>
                                setIsCalculationGuideExpanded(value => !value)
                            }
                        >
                            {isCalculationGuideExpanded ? 'Thu gọn' : 'Xem thêm'}
                        </Button>
                    </Space>
                }
            />
            {/* Tổng quan hoa hồng */}
            <Row gutter={16} justify="space-between" style={{ margin: 0 }}>
                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Tổng hoa hồng tháng"
                        value={summary.gross_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>

                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Còn được nhận trong tháng"
                        value={summary.payable_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>

                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Đã chi trả của khoản trong tháng"
                        value={summary.paid_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>

                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Hoa hồng dự kiến"
                        value={summary.upcoming_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>

                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Tổng đang giữ lại"
                        value={summary.hold_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>

                <Card size="small" className="commission-summary-statistic">
                    <StatisticNumber
                        title="Khấu trừ trong kỳ"
                        value={summary.deduction_amount || 0}
                        precision={0}
                        unit="đ"
                    />
                </Card>
            </Row>

            <Space
                direction="vertical"
                size={16}
                style={{
                    width: '100%',
                }}
            >
                {/* Sổ hoa hồng */}
                <Card
                    size="small"
                    headStyle={sectionHeadStyle}
                    bodyStyle={sectionBodyStyle}
                    title={
                        <Space
                            direction="vertical"
                            size={0}
                        >
                            <Text strong>
                                {`Sổ hoa hồng tháng ${periodLabel || '—'
                                    }`}
                            </Text>

                            {selectedEmployee && (
                                <Text
                                    strong
                                    style={{
                                        fontSize: 13,
                                        fontWeight:
                                            'normal',
                                    }}
                                >
                                    {
                                        selectedEmployee.full_name
                                    }
                                </Text>
                            )}
                        </Space>
                    }
                >
                    <Table
                        className="commission-responsive-table"
                        rowKey="id"
                        size="small"
                        tableLayout="fixed"
                        loading={sectionLoading.ledger}
                        dataSource={ledger.data || []}
                        columns={responsiveColumns(ledgerColumns)}
                        pagination={{
                            ...paginationProps(ledger, 'ledger', fetchCommissionDashboardLedger),
                            showTotal: isMobile
                                ? undefined
                                : total =>
                                    `Tổng ${total} khoản hoa hồng`,
                        }}
                        locale={{
                            emptyText: (
                                <Empty description="Chưa có khoản hoa hồng nào trong tháng này" />
                            ),
                        }}
                    />
                </Card>

                {/* Tiến độ thanh toán dịch vụ */}
                <Card
                    size="small"
                    headStyle={sectionHeadStyle}
                    bodyStyle={sectionBodyStyle}
                    title="Tiến độ thanh toán dịch vụ hiện tại"
                >
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                        <Col xs={12} md={6}>
                            <StatisticNumber
                                title="Tổng nghĩa vụ"
                                value={payments.summary?.net_obligation || 0}
                                precision={0}
                                unit="đ"
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <StatisticNumber
                                title="Đã phân bổ"
                                value={payments.summary?.allocated_amount || 0}
                                precision={0}
                                unit="đ"
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <StatisticNumber
                                title="Còn phải thu"
                                value={payments.summary?.outstanding_amount || 0}
                                precision={0}
                                unit="đ"
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <StatisticNumber
                                title="Tiến độ chung"
                                value={payments.summary?.payment_progress || 0}
                                precision={2}
                                unit="%"
                            />
                        </Col>
                    </Row>
                    <Table
                        className="commission-responsive-table"
                        rowKey="customer_service_id"
                        size="small"
                        tableLayout="fixed"
                        loading={sectionLoading.payments}
                        pagination={false}
                        dataSource={payments.data || []}
                        columns={responsiveColumns(paymentColumns)}
                        locale={{
                            emptyText: (
                                <Empty description="Chưa có dịch vụ liên quan đến nhân viên" />
                            ),
                        }}
                    />
                </Card>

                {/* Hoa hồng dự kiến */}
                <Card
                    size="small"
                    headStyle={sectionHeadStyle}
                    bodyStyle={sectionBodyStyle}
                    title={
                        <Space>
                            <Space direction="vertical" size={0}>
                                <Text strong>Hoa hồng dự kiến</Text>
                                <Text type="secondary">Toàn bộ khoản có khả năng phát sinh trong tương lai</Text>
                            </Space>
                        </Space>
                    }
                    extra={
                        <>
                            {Number(
                                summary.upcoming_amount
                            ) > 0 && (
                                    <Text strong>
                                        {formatCommissionMoney(
                                            summary.upcoming_amount
                                        )}
                                    </Text>
                                )}
                        </>
                    }
                >
                    <Table
                        className="commission-responsive-table"
                        rowKey={row =>
                            `${row.customer_service_id}:${row.role_key}:${row.component_key}:${row.action_id}:${row.condition_code}`
                        }
                        size="small"
                        tableLayout="fixed"
                        loading={sectionLoading.upcoming}
                        pagination={paginationProps(upcoming, 'upcoming', fetchCommissionDashboardUpcoming)}
                        dataSource={upcoming.data || []}
                        columns={responsiveColumns(upcomingColumns)}
                        locale={{
                            emptyText: (
                                <Empty description="Hiện không có khoản hoa hồng nào đang chờ điều kiện" />
                            ),
                        }}
                    />
                </Card>

                <Row gutter={[16, 16]}>
                    {/* Hoa hồng tạm giữ */}
                    <Col
                        xs={24}
                        lg={24}
                    >
                        <Card
                            size="small"
                            headStyle={
                                sectionHeadStyle
                            }
                            bodyStyle={
                                sectionBodyStyle
                            }
                            title="Khoản công ty đang giữ lại"
                            style={{
                                height: '100%',
                            }}
                        >
                            {holds.data
                                ?.length ? (
                                <Table
                                    className="commission-responsive-table"
                                    rowKey="hold_id"
                                    size="small"
                                    tableLayout="fixed"
                                    loading={sectionLoading.holds}
                                    pagination={paginationProps(holds, 'holds', fetchCommissionDashboardHolds)}
                                    dataSource={holds.data}
                                    columns={responsiveColumns([
                                        {
                                            title: 'Khách hàng / dịch vụ',
                                            key: 'service',
                                            render: (_, item) => <Space direction="vertical" size={2}>
                                                <CommissionCustomerColumn customer={item.customer} />
                                                <CommissionServiceColumn customerServiceId={item.customer_service_id} service={item.service} />
                                            </Space>,
                                        },
                                        {
                                            title: 'Lý do tạm giữ',
                                            dataIndex:
                                                'reason',
                                            render:
                                                value =>
                                                    value ||
                                                    'Chờ đủ điều kiện',
                                        },
                                        {
                                            title: 'Điều kiện giải phóng',
                                            dataIndex: 'release_condition',
                                            width: '27%',
                                            render: value => value || 'Chờ điều kiện mở giữ của chính sách',
                                        },
                                        {
                                            title: 'Số tiền',
                                            dataIndex:
                                                'hold_amount',
                                            width: '16%',
                                            align: 'right',
                                            render:
                                                formatCommissionMoney,
                                        },
                                    ])}
                                />
                            ) : (
                                <Empty description="Không có khoản hoa hồng nào đang tạm giữ" />
                            )}
                        </Card>
                    </Col>

                    {/* Khấu trừ / điều chỉnh */}
                    <Col
                        xs={24}
                        lg={24}
                    >
                        <Card
                            size="small"
                            headStyle={
                                sectionHeadStyle
                            }
                            bodyStyle={
                                sectionBodyStyle
                            }
                            title={`Khấu trừ / điều chỉnh${periodLabel
                                ? ` tháng ${periodLabel}`
                                : ''
                                }`}
                            style={{
                                height: '100%',
                            }}
                        >
                            {adjustments.data
                                ?.length ? (
                                <Table
                                    className="commission-responsive-table"
                                    rowKey="id"
                                    size="small"
                                    tableLayout="fixed"
                                    loading={sectionLoading.adjustments}
                                    pagination={paginationProps(adjustments, 'adjustments', fetchCommissionDashboardAdjustments)}
                                    dataSource={adjustments.data}
                                    columns={responsiveColumns([
                                        {
                                            title: 'Loại điều chỉnh',
                                            dataIndex: 'adjustment_type_label',
                                            render:
                                                value =>
                                                    value ||
                                                    'Điều chỉnh hoa hồng',
                                        },
                                        {
                                            title: 'Nguồn phát sinh',
                                            dataIndex: 'origin_label',
                                            width: '23%',
                                            render: value => value || 'Điều chỉnh theo đề nghị nghiệp vụ',
                                        },
                                        {
                                            title: 'Lý do',
                                            dataIndex: 'reason',
                                            width: '25%',
                                            render: value => typeof value === 'string' && value.trim()
                                                ? value
                                                : 'Không có mô tả bổ sung',
                                        },
                                        {
                                            title: 'Trạng thái',
                                            dataIndex: 'status_label',
                                            width: '14%',
                                            render: value => value || 'Chưa xác định trạng thái',
                                        },
                                        {
                                            title: 'Số tiền',
                                            dataIndex:
                                                'amount',
                                            width: '18%',
                                            align: 'right',
                                            render:
                                                formatCommissionMoney,
                                        },
                                    ])}
                                />
                            ) : (
                                <Empty description="Không có khấu trừ hoặc điều chỉnh trong tháng" />
                            )}
                        </Card>
                    </Col>
                </Row>
            </Space>

            <CommissionDashboardDetailDrawer
                itemId={detailItemId}
                visible={!!detailItemId}
                onClose={() =>
                    setDetailItemId(null)
                }
            />
        </div>
        </CommissionCustomerServiceDetailProvider>
    );
}
